import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Link, Typography } from '@one-thd/sui-atomic-components';
import {
  initialSmsTelemarketingTableData,
  PHONE_TITLE, PHONE_TYPE,
  PRO_TITLE,
  PROMOTIONS_TITLE
} from './tableData';
import MarketingPreferencesCard from './common/MarketingPreferencesCard';
import {
  ALL_SMS_TELEMARKETING_UNSUBSCRIBE_SUCCESS,
  DO_NOT_CALL,
  NO,
  NO_PHONE_TEXT,
  PENDING,
  PRO_PENDING,
  PRO_PROMO_PENDING,
  PROMO_PENDING,
  SECURITY_PATH,
  SMS_OPT_IN_SUCCESS,
  YES
} from '../constants';
import {
  retrieveSmsPreferences,
  unsubscribeFromAllMessages,
} from '../apis/sms';

import {
  handleRollback,
  setupHandleEmailOrSmsTelemarketingRetrieve
} from '../apis/common';
import { BannerContext, BannerProvider } from '../contexts/BannerContext';
import { addAnalyticEvent } from '../analytics/common';
import {
  confirmConsentEvent,
  confirmConsentOverlay,
  unsubscribeFromAllSms,
  useAccountPhoneOverlay
} from '../analytics/sms';
import ErrorBoundary from '../contexts/ErrorBoundary';
import ConfirmationModal from './SmsModals/ConfirmationModal';
import AccountPhoneModal from './SmsModals/AccountPhoneModal';
import {
  containsOptIn, containsTelemarketing,
  scrollToBanner
} from '../../utils/general';
import { formatPhone, mergeSmsAndPhoneData } from './common/helper';
import { RetrieveErrorContext } from '../contexts/RetrieveErrorContext';
import { retrieveTelemarketingPreferences, unsubscribeTelemarketing } from '../apis/telemarketing';

export const smsSaveButton = 'sms-save-button';
export const unsubSmsButton = 'sms-unsub';

const Actions = {
  RESET: 'RESET',
  USE_ACCOUNT_PHONE: 'USE_ACCOUNT_PHONE',
  CONFIRMATION_MODAL: 'CONFIRMATION_MODAL',
  UNSUBSCRIBE_FROM_ALL: 'UNSUBSCRIBE_FROM_ALL'
};

function initialState() {
  return {
    noPhoneModalHeader: '',
    showUseAccountPhone: false,
    showConfirmationModal: false,
    showProLegal: false,
    showPromoLegal: false,
    displayUnsubscribeModal: false
  };
}

function reducer(state, action) {
  switch (action.type) {
  case Actions.USE_ACCOUNT_PHONE:
    addAnalyticEvent(useAccountPhoneOverlay);
    return {
      ...state,
      showUseAccountPhone: true
    };
  case Actions.CONFIRMATION_MODAL:
    addAnalyticEvent(confirmConsentOverlay);
    return {
      ...state,
      showUseAccountPhone: false,
      showProLegal: action.showProLegal,
      showPromoLegal: action.showPromoLegal,
      showConfirmationModal: true,
    };
  default:
    return initialState();
  }
}

export function Preferences({ phoneNumber, isB2B }) {
  const {
    openErrorAlert,
    openSuccessAlert,
    closeSuccessAlert,
    openInfoAlert,
    openWarningAlert,
    openPendingAlert,
    closePendingAlert,
    closeSmsTelemarketingInfoAlert
  } = useContext(BannerContext);

  const {
    addError
  } = useContext(RetrieveErrorContext);

  const [tableData, setTableData] = React.useState(initialSmsTelemarketingTableData());
  const [selected, setSelected] = React.useState([]);
  const [initialSubs, setInitialSubs] = React.useState([]);
  const [state, dispatch] = React.useReducer(reducer, undefined, initialState);
  const formattedPhone = formatPhone(phoneNumber);
  const handleSmsTelemarketingRetrieve = setupHandleEmailOrSmsTelemarketingRetrieve(
    tableData, setTableData, setSelected, setInitialSubs
  );
  const [telemarketingCard, setTelemarketingCard] = React.useState(false);
  const scrollToAlert = useRef(null);
  const AlertInfo = () => (
    <Typography variant="body-base" ref={scrollToAlert} data-testid="alert-info">
      {NO_PHONE_TEXT}
      <Link href={SECURITY_PATH}>Security</Link>
      {' page and add a phone number to your account.'}
    </Typography>
  );

  const determinePendingText = () => {
    const pro = initialSubs.find((item) => item && item.type === 'PRO')?.status;
    const promotion = initialSubs.find((item) => item && item.type === 'PROMOTIONS')?.status;
    if (pro === PENDING && promotion === PENDING) {
      return PRO_PROMO_PENDING;
    } if (pro === PENDING) {
      return PRO_PENDING;
    } if (promotion === PENDING) {
      return PROMO_PENDING;
    }
    return null;
  };

  React.useEffect(() => {
    if (initialSubs.length > 0) {
      openPendingAlert(determinePendingText(), phoneNumber, tableData);
    }
  }, [initialSubs]);

  function handleError() {
    handleRollback(tableData, setTableData, selected, setSelected);
    openErrorAlert();
    dispatch({ type: Actions.RESET });
  }

  function handleRetrieveError() {
    addError();
  }

  React.useEffect(() => {
    if (tableData[PHONE_TITLE].value === NO) {
      setTelemarketingCard(true);
    }
  }, [initialSubs]);

  React.useEffect(() => {
    if (phoneNumber) {
      const fetchData = async () => {
        try {
          const fetchSmsData = () => new Promise((resolve) => {
            retrieveSmsPreferences(phoneNumber, resolve, handleRetrieveError);
          });
          const fetchPhoneData = () => new Promise((resolve) => {
            retrieveTelemarketingPreferences(phoneNumber, resolve, handleRetrieveError);
          });
          const [smsData, phoneData] = await Promise.all([fetchSmsData(), fetchPhoneData()]);
          const mergedSubscriptions = mergeSmsAndPhoneData(smsData, phoneData);
          handleSmsTelemarketingRetrieve(mergedSubscriptions);

        } catch (error) {
          addError(error);
        }
      };
      fetchData();
    } else {
      openInfoAlert(<AlertInfo />);
    }
  }, [phoneNumber]);

  const handleOptInSuccess = (res) => {
    addAnalyticEvent(confirmConsentEvent);
    openSuccessAlert(SMS_OPT_IN_SUCCESS);
    handleSmsTelemarketingRetrieve(res);
    dispatch({ type: Actions.RESET });
  };

  function isOptIn(title) {
    return tableData[title]?.value === YES;
  }

  const handleClose = () => {
    handleRollback(tableData, setTableData, selected, setSelected);
    dispatch({ type: Actions.RESET });
  };

  const unsubscribeAllSmsTelemarketing = () => {
    const unsubData = async () => {
      try {
        const unsubSms = () => new Promise((resolve) => {
          unsubscribeFromAllMessages(phoneNumber, resolve, handleError);
        });
        const unsubPhone = () => new Promise((resolve) => {
          unsubscribeTelemarketing(phoneNumber, resolve, handleError);
        });
        const [smsUnsub, phoneUnsub] = await Promise.all([unsubSms(), unsubPhone()]);

        const mergedUnsubs = smsUnsub;
        if (phoneUnsub?.data?.permissionToContact === NO) {
          mergedUnsubs.data.subscriptions.push({ type: PHONE_TYPE, status: NO });
        }
        setTelemarketingCard(true);
        openSuccessAlert(ALL_SMS_TELEMARKETING_UNSUBSCRIBE_SUCCESS, phoneNumber);
        handleSmsTelemarketingRetrieve(mergedUnsubs);
      } catch (error) {
        handleError();
      }
    };
    unsubData();
  };

  const clickSaveChanges = () => {
    closeSuccessAlert();
    closeSmsTelemarketingInfoAlert();
    if (!phoneNumber) {
      openWarningAlert(<AlertInfo />);
      scrollToBanner(scrollToAlert);
    } else if (selected.length) {
      if (containsOptIn(selected, tableData) && !containsTelemarketing(selected)) {
        dispatch({ type: Actions.USE_ACCOUNT_PHONE });
      } else {
        closePendingAlert();
        closeSuccessAlert();
        unsubscribeAllSmsTelemarketing();
      }
    }
  };
  const handleUnsubscribeFromAllClick = () => {
    if (!phoneNumber) {
      openWarningAlert(<AlertInfo />);
      scrollToBanner(scrollToAlert);
    } else {
      closeSuccessAlert();
      closePendingAlert();
      closeSmsTelemarketingInfoAlert();
      addAnalyticEvent(unsubscribeFromAllSms);
      unsubscribeAllSmsTelemarketing();
    }
  };

  const handleContinue = () => {
    dispatch({
      type: Actions.CONFIRMATION_MODAL,
      showProLegal: isOptIn(PRO_TITLE),
      showPromoLegal: isOptIn(PROMOTIONS_TITLE)
    });
  };
  const optOutB2BText = ' If you opt out of marketing text messages, '
    + ' you may still receive transactional communications.';
  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">MARKETING TEXT MESSAGES & PHONE CALLS</Typography>
      <Typography variant="body-base" height="none" aria-label="description">
        {'We will text you information about how to opt into (and out of) marketing text messages'
          + ' when you save changes. By opting out of Phone Calls, you will be added to The Home Depot’s'
          + ' Do Not Call list. The Home Depot may still call you on non-promotional and non-marketing'
          + ' matters. If wishing to learn more, view the '}
        <Link underline="always" href={DO_NOT_CALL}>Do Not Call Policy</Link>
        {'. To edit your preferences,'
          + ' click on the individual preference options below. An opt out of one option will be treated as'
          + ' an opt out of all preferences below.'}
        {isB2B && optOutB2BText}
      </Typography>
      <MarketingPreferencesCard
        tableData={tableData}
        setTableData={setTableData}
        setSelected={setSelected}
        telemarketingCard={telemarketingCard}
      />
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden xs:sui-col-span-4 sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
          <Button aria-label={smsSaveButton} fullWidth onClick={clickSaveChanges}>
            Save Changes
          </Button>
          <Button
            aria-label={unsubSmsButton}
            fullWidth
            variant="secondary"
            onClick={handleUnsubscribeFromAllClick}
          >
            Opt Out From All Marketing Texts & Phone Calls
          </Button>
        </div>
      </div>
      {state.showConfirmationModal && (
        <ConfirmationModal
          phoneNumber={phoneNumber}
          handleClose={handleClose}
          handleOptInSuccess={handleOptInSuccess}
          tableData={tableData}
          selected={selected}
          proLegalState={state.showProLegal}
          promoLegalState={state.showPromoLegal}
        />
      )}
      {state.showUseAccountPhone && (
        <AccountPhoneModal
          phoneNumber={phoneNumber}
          handleClose={handleClose}
          handleContinue={handleContinue}
        />
      )}
    </div>
  );

}

function SmsTelemarketingPreferences({ phoneNumber, isB2B }) {

  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences phoneNumber={phoneNumber} isB2B={isB2B} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default SmsTelemarketingPreferences;

const propTypes = {
  phoneNumber: PropTypes.string,
  isB2B: PropTypes.bool
};

const defaultProps = {
  phoneNumber: null,
  isB2B: false
};

SmsTelemarketingPreferences.propTypes = propTypes;
SmsTelemarketingPreferences.defaultProps = defaultProps;
Preferences.propTypes = propTypes;
Preferences.defaultProps = defaultProps;
